/**
 * itemContext.ts
 *
 * Wraps OfficeCtx.mailbox's ItemChanged subscription in a tiny
 * host-agnostic event emitter, plus a React hook so App.tsx can reset its
 * filing flow whenever the selected email changes (or becomes null — a
 * pinned taskpane can be open with nothing selected).
 */
import { useEffect, useState } from "react";

import type { MailItemLike, OfficeCtx } from "./officeShim";

type Listener = (item: MailItemLike | null) => void;

export class ItemContext {
  private readonly listeners = new Set<Listener>();
  private current: MailItemLike | null;

  constructor(private readonly ctx: OfficeCtx) {
    this.current = ctx.mailbox.getCurrentItem();
    this.ctx.mailbox.addItemChangedHandler(this.handleChanged);
  }

  private readonly handleChanged = (): void => {
    this.current = this.ctx.mailbox.getCurrentItem();
    for (const listener of this.listeners) {
      listener(this.current);
    }
  };

  getCurrent(): MailItemLike | null {
    return this.current;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  dispose(): void {
    this.ctx.mailbox.removeItemChangedHandler();
    this.listeners.clear();
  }
}

/** Reflects the currently-selected mail item as React state, updating on
 * every Office ItemChanged event. */
export function useCurrentItem(itemContext: ItemContext): MailItemLike | null {
  const [item, setItem] = useState<MailItemLike | null>(() =>
    itemContext.getCurrent(),
  );

  useEffect(() => {
    setItem(itemContext.getCurrent());
    return itemContext.subscribe(setItem);
  }, [itemContext]);

  return item;
}
